import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Search, RefreshCw, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import img1 from '../../../assets/attendenceCarousel.png';
import { attendanceApi } from '../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────

type EmpType = 'permanent' | 'contractual';

interface DayStatus {
  status: 'P' | 'A' | 'H' | 'NM' | 'WE' | 'FU';
}

interface EmployeeRow {
  empId: string | number;
  empName: string;
  empType: string;
  designation: string;
  kvName: string;
  roName: string;
  stateName: string;
  totalPresent: number;
  totalAbsent: number;
  totalHalfDay: number;
  attendancePercent: number;
  days: Record<number, DayStatus>;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_META: Record<string, { label: string; bg: string; text: string }> = {
  P:  { label: 'P',  bg: 'bg-green-100',  text: 'text-green-700'  },
  A:  { label: 'A',  bg: 'bg-red-100',    text: 'text-red-700'    },
  H:  { label: 'H',  bg: 'bg-cyan-100',   text: 'text-cyan-700'   },
  NM: { label: 'NM', bg: 'bg-yellow-100', text: 'text-yellow-700' },
  WE: { label: 'WE', bg: 'bg-blue-100',   text: 'text-blue-500'   },
  FU: { label: '—',  bg: 'bg-gray-50',    text: 'text-gray-300'   },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function isWeekend(year: number, month: number, day: number): boolean {
  const dow = new Date(year, month - 1, day).getDay();
  return dow === 0 || dow === 6;
}

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function getDayName(year: number, month: number, day: number): string {
  return DAY_ABBR[new Date(year, month - 1, day).getDay()];
}

/**
 * Maps a record from /pis/monthlyemployeeattendance (permanent employees).
 * Uses the calendar.status field in each detailedAttendance entry:
 *   11 = WORKING, 12 = SUNDAY, 13 = SECOND SATURDAY, 14+ = HOLIDAY
 */
function mapPermanentRow(raw: any, year: number, month: number): EmployeeRow {
  const details = raw.details ?? raw;
  const attendance = details.attendance ?? {};
  const detailedAttendance: any[] = attendance.detailedAttendance ?? [];

  // Build map: day-of-month → { calendarStatus, attended }
  const dayCalendar = new Map<number, { calendarStatus: number; attended: boolean }>();
  for (const entry of detailedAttendance) {
    const d = new Date(entry.attendanceDate).getDate();
    dayCalendar.set(d, {
      calendarStatus: entry.calendar?.status ?? 11,
      attended: String(entry.status) === '1',
    });
  }

  const totalDaysCount = daysInMonth(year, month);
  const today = new Date();
  const days: Record<number, DayStatus> = {};

  for (let d = 1; d <= totalDaysCount; d++) {
    if (new Date(year, month - 1, d) > today) {
      days[d] = { status: 'FU' };
    } else {
      const cal = dayCalendar.get(d);
      if (!cal) {
        // Day absent from API — fall back to weekend check
        days[d] = isWeekend(year, month, d) ? { status: 'WE' } : { status: 'NM' };
      } else if (cal.calendarStatus === 12 || cal.calendarStatus === 13) {
        // Sunday (12) or Second Saturday (13)
        days[d] = { status: 'WE' };
      } else if (cal.calendarStatus !== 11) {
        // Gazetted Holiday or any other non-working calendar status
        days[d] = { status: 'H' };
      } else {
        // Regular working day — use attendance
        days[d] = { status: cal.attended ? 'P' : 'A' };
      }
    }
  }

  const totalPresent = Number(attendance.totalPresent) || 0;
  const totalAbsent = Number(attendance.totalAbsent) ||
    Object.values(days).filter(v => v.status === 'A').length;
  const totalHalfDay = Number(attendance.totalHalfDay) || 0;

  const workingDays = Object.values(days).filter(
    v => v.status !== 'WE' && v.status !== 'FU' && v.status !== 'H',
  ).length;
  const attendancePercent = workingDays > 0
    ? Math.round((totalPresent / workingDays) * 100)
    : 0;

  return {
    empId: details.empCode ?? details.empId ?? '',
    empName: details.empName ?? '—',
    empType: 'Permanent',
    designation: details.designation ?? details.post_name ?? details.postName ?? '—',
    kvName: details.kvSchoolName || details.kvName || '—',
    roName: details.roName ?? details.kvRoName ?? '—',
    stateName: details.stateName ?? '—',
    totalPresent,
    totalAbsent,
    totalHalfDay,
    attendancePercent,
    days,
  };
}

/** Maps a record from /pis/employeeattendance (contractual). */
function mapContractualRow(raw: any, year: number, month: number): EmployeeRow {
  const totalDaysCount = daysInMonth(year, month);
  const today = new Date();
  const days: Record<number, DayStatus> = {};

  for (let d = 1; d <= totalDaysCount; d++) {
    if (new Date(year, month - 1, d) > today) {
      days[d] = { status: 'FU' };
    } else if (isWeekend(year, month, d)) {
      days[d] = { status: 'WE' };
    } else {
      const dayKey = String(d).padStart(2, '0');
      const rawStatus =
        raw.days?.[dayKey] ?? raw.days?.[d] ??
        raw.attendance?.[dayKey] ?? raw.attendance?.[d] ??
        raw.dailyStatus?.[dayKey] ?? raw.dailyStatus?.[d] ??
        null;
      if (rawStatus === null || rawStatus === undefined) {
        days[d] = { status: 'NM' };
      } else {
        const s = String(rawStatus).toUpperCase();
        if (s === 'P' || s === 'PRESENT' || s === '1' || s === '0.5') days[d] = { status: 'P' };
        else if (s === 'A' || s === 'ABSENT' || s === '0') days[d] = { status: 'A' };
        else if (s === 'H' || s === 'HOLIDAY') days[d] = { status: 'H' };
        else days[d] = { status: 'NM' };
      }
    }
  }

  const totalPresent =
    typeof raw.totalPresent === 'number' ? raw.totalPresent :
    typeof raw.totalAttendance === 'number' ? raw.totalAttendance :
    Object.values(days).filter(v => v.status === 'P').length;

  const totalAbsent =
    typeof raw.totalAbsent === 'number' ? raw.totalAbsent :
    Object.values(days).filter(v => v.status === 'A').length;

  const workingDays = Object.values(days).filter(
    v => v.status !== 'WE' && v.status !== 'FU' && v.status !== 'H',
  ).length;
  const attendancePercent = workingDays > 0
    ? Math.round((totalPresent / workingDays) * 100)
    : 0;

  return {
    empId: raw.empId ?? raw.empid ?? raw.id ?? '',
    empName: raw.empName ?? raw.emp_name ?? raw.name ?? '—',
    empType: 'Contractual',
    designation: raw.designation ?? raw.post_name ?? raw.postName ?? '—',
    kvName: raw.kvName ?? raw.kv_name ?? raw.schoolName ?? '—',
    roName: raw.roName ?? '—',
    stateName: raw.stateName ?? '—',
    totalPresent,
    totalAbsent,
    totalHalfDay: 0,
    attendancePercent,
    days,
  };
}

/** Export visible rows to CSV */
function exportCSV(employees: EmployeeRow[], year: number, month: number) {
  const totalDays = daysInMonth(year, month);
  const dayHeaders = Array.from({ length: totalDays }, (_, i) => String(i + 1).padStart(2, '0'));
  const headers = ['S.No', 'Employee Name', 'Emp ID', 'Type', 'Designation', 'KV Name', 'RO', 'State', ...dayHeaders, 'Present', 'Absent', 'Half Day', 'Att %'];

  const rows = employees.map((emp, idx) => {
    const dayValues = Array.from({ length: totalDays }, (_, i) => emp.days[i + 1]?.status ?? '');
    return [
      idx + 1, emp.empName, emp.empId, emp.empType, emp.designation, emp.kvName,
      emp.roName, emp.stateName,
      ...dayValues, emp.totalPresent, emp.totalAbsent, emp.totalHalfDay, emp.attendancePercent + '%',
    ];
  });

  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `staff-monthly-attendance-${MONTHS[month - 1]}-${year}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function StaffMonthlyAttendance() {
  const navigate = useNavigate();
  const carouselImages = [img1];

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [empType, setEmpType] = useState<EmpType>('permanent');
  const [search, setSearch] = useState('');
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [jumpPage, setJumpPage] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState<{ current: number; total: number } | null>(null);

  // Two-digit year the API expects (2025 → 25)
  const apiYear = selectedYear % 100;

  const fetchData = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      let res: any = null;

      if (empType === 'permanent') {
        res = await attendanceApi.getMonthlyEmployeeAttendance(page, apiYear, selectedMonth);
      } else {
        res = await attendanceApi.getContractualEmployeeAttendance(page, apiYear, selectedMonth);
      }

      if (!res) {
        setError('No data returned from the server.');
        setEmployees([]);
        setTotalPages(1);
        setTotalRecords(0);
        return;
      }

      const pagination = res.pagination ?? {};
      setTotalPages(pagination.totalPages ?? 1);
      setTotalRecords(pagination.total ?? res.total ?? 0);
      setCurrentPage(page);

      const rawList: any[] =
        Array.isArray(res.data) ? res.data :
        Array.isArray(res.employees) ? res.employees :
        Array.isArray(res.records) ? res.records :
        Array.isArray(res) ? res :
        [];

      const mapped = empType === 'permanent'
        ? rawList.map(r => mapPermanentRow(r, selectedYear, selectedMonth))
        : rawList.map(r => mapContractualRow(r, selectedYear, selectedMonth));

      setEmployees(mapped);
    } catch {
      setError('Failed to load attendance data. Please try again.');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, empType]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || loading) return;
    fetchData(page);
  };

  const handleJumpToPage = () => {
    if (loading) return;
    const page = Number(jumpPage);
    if (!Number.isInteger(page) || page < 1 || page > totalPages) return;
    setJumpPage('');
    handlePageChange(page);
  };

  const handleExportCSV = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    setExportProgress({ current: 0, total: 1 });
    try {
      const getRawList = (res: any): any[] =>
        Array.isArray(res.data) ? res.data :
        Array.isArray(res.employees) ? res.employees :
        Array.isArray(res.records) ? res.records :
        Array.isArray(res) ? res : [];
      const mapRow = empType === 'permanent'
        ? (r: any) => mapPermanentRow(r, selectedYear, selectedMonth)
        : (r: any) => mapContractualRow(r, selectedYear, selectedMonth);

      const first = empType === 'permanent'
        ? await attendanceApi.getMonthlyEmployeeAttendance(1, apiYear, selectedMonth)
        : await attendanceApi.getContractualEmployeeAttendance(1, apiYear, selectedMonth);

      if (!first) return;

      const pages = first.pagination?.totalPages ?? 1;
      let allRows: EmployeeRow[] = getRawList(first).map(mapRow);
      setExportProgress({ current: 1, total: pages });

      for (let p = 2; p <= pages; p++) {
        const res = empType === 'permanent'
          ? await attendanceApi.getMonthlyEmployeeAttendance(p, apiYear, selectedMonth)
          : await attendanceApi.getContractualEmployeeAttendance(p, apiYear, selectedMonth);
        if (res) allRows = [...allRows, ...getRawList(res).map(mapRow)];
        setExportProgress({ current: p, total: pages });
      }

// Deduplicate by empId — the API may return the same employee
      // across multiple pages (pagination drift / server-side bug).
      // On screen this is invisible because only one page is rendered at a time.
      const seen = new Set<string | number>();
      const uniqueRows = allRows.filter(row => {
        const key = row.empId;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      exportCSV(uniqueRows, selectedYear, selectedMonth);
    } finally {
      setExportLoading(false);
      setExportProgress(null);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return employees;
    const q = search.trim().toLowerCase();
    return employees.filter(
      e =>
        e.empName.toLowerCase().includes(q) ||
        String(e.empId).includes(q) ||
        e.kvName.toLowerCase().includes(q) ||
        e.designation.toLowerCase().includes(q)
    );
  }, [employees, search]);

  const totalDays = daysInMonth(selectedYear, selectedMonth);
  const dayNumbers = Array.from({ length: totalDays }, (_, i) => i + 1);

  const yearOptions = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  const stats = useMemo(() => ({
    total: totalRecords,
    pageCount: filtered.length,
    avgPresent: filtered.length
      ? Math.round(filtered.reduce((s, e) => s + e.totalPresent, 0) / filtered.length)
      : 0,
    avgPercent: filtered.length
      ? Math.round(filtered.reduce((s, e) => s + e.attendancePercent, 0) / filtered.length)
      : 0,
  }), [filtered, totalRecords]);

  const exportPct = exportProgress
    ? Math.round((exportProgress.current / exportProgress.total) * 100)
    : 0;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">

      {/* Export Progress Modal */}
      {exportLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-5 min-w-[340px] max-w-sm w-full">
            <div className="flex items-center gap-3 text-[#6A1786]">
              <Download className="w-6 h-6 animate-bounce" />
              <span className="text-lg font-bold">Preparing Export</span>
            </div>
            <p className="text-sm text-gray-500 text-center">
              Fetching {MONTHS[selectedMonth - 1]} {selectedYear} records&hellip;
            </p>
            {/* Bar */}
            <div className="w-full">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>
                  {exportProgress
                    ? `Page ${exportProgress.current} of ${exportProgress.total}`
                    : 'Connecting…'}
                </span>
                <span className="font-semibold text-[#6A1786]">{exportPct}%</span>
              </div>
              <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#6A1786] to-purple-400 rounded-full transition-all duration-300"
                  style={{ width: `${exportPct}%` }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-400">Please wait, do not close this window</p>
          </div>
        </div>
      )}
      <Header />
      <HeroCarousel images={carouselImages} />

      <div className="flex-1 w-full px-4 md:px-10 lg:px-20 py-6">
        {/* Breadcrumb */}
        <Breadcrumb />

        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mt-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#6A1786]">Staff Monthly Attendance</h1>
              <p className="text-sm text-gray-500">Day-wise attendance register · {MONTHS[selectedMonth - 1]} {selectedYear}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchData(currentPage)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleExportCSV}
              disabled={exportLoading || totalRecords === 0}
              className="inline-flex items-center gap-2 px-3 py-2 bg-[#6A1786] text-white rounded-lg hover:bg-[#7a1f96] transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {exportLoading ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-5">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Month */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Month</label>
              <select
                value={selectedMonth}
                onChange={e => setSelectedMonth(Number(e.target.value))}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              >
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>

            {/* Year */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Year</label>
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(Number(e.target.value))}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            {/* Employee Type — Permanent | Contractual */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Employee Type</label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                {(['permanent', 'contractual'] as EmpType[]).map(type => (
                  <button
                    key={type}
                    onClick={() => setEmpType(type)}
                    className={`px-4 py-2 capitalize transition font-medium ${
                      empType === type
                        ? 'bg-[#6A1786] text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Search */}
            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Search (this page)</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Name, ID, KV, RO, State..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Summary strip */}
        <div className="flex flex-wrap gap-4 mb-4">
          {[
            { label: 'Total Records', value: stats.total.toLocaleString() },
            { label: 'This Page', value: stats.pageCount },
            { label: 'Avg. Present Days', value: stats.avgPresent },
            { label: 'Avg. Attendance', value: stats.avgPercent + '%' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-lg border border-gray-100 shadow-sm px-4 py-3 flex flex-col min-w-[120px]">
              <span className="text-xs text-gray-500 font-medium">{s.label}</span>
              <span className="text-lg font-bold text-[#6A1786]">{s.value}</span>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-4">
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <span key={key} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded ${meta.bg} ${meta.text}`}>
              <span className="font-bold">{meta.label}</span>
              <span className="font-normal text-gray-500">
                {key === 'P' ? 'Present' : key === 'A' ? 'Absent' : key === 'H' ? 'Holiday' : key === 'NM' ? 'Not Marked' : key === 'WE' ? 'Weekend' : 'Future'}
              </span>
            </span>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-500">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading attendance data...
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500 gap-3">
              <Calendar className="w-10 h-10 text-gray-300" />
              <p className="text-sm">{error}</p>
              <button onClick={() => fetchData(1)} className="text-sm text-[#6A1786] hover:underline">Try again</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
              <Calendar className="w-10 h-10" />
              <p className="text-sm">No attendance records found for the selected filters.</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
              <table className="w-full text-left border-collapse text-xs">
                <thead className="sticky top-0 z-20 bg-[#6A1786] text-white">
                  <tr>
                    <th className="py-3 px-3 font-semibold whitespace-nowrap sticky left-0 z-30 bg-[#6A1786]">S.No</th>
                    <th className="py-3 px-3 font-semibold whitespace-nowrap sticky left-10 z-30 bg-[#6A1786] min-w-[160px]">Employee Name</th>
                    <th className="py-3 px-3 font-semibold whitespace-nowrap">Emp ID</th>
                    <th className="py-3 px-3 font-semibold whitespace-nowrap min-w-[140px]">KV Name</th>
                    <th className="py-3 px-3 font-semibold whitespace-nowrap min-w-[100px]">RO</th>
                    <th className="py-3 px-3 font-semibold whitespace-nowrap min-w-[100px]">State</th>

                    {/* Day columns */}
                    {dayNumbers.map(d => (
                      <th
                        key={d}
                        className={`py-2 px-2 font-semibold text-center w-9 ${isWeekend(selectedYear, selectedMonth, d) ? 'bg-[#5a1070]' : ''}`}
                      >
                        <div className="flex flex-col items-center leading-tight gap-0.5">
                          <span>{String(d).padStart(2, '0')}</span>
                          <span className="text-[9px] font-normal opacity-70">{getDayName(selectedYear, selectedMonth, d)}</span>
                        </div>
                      </th>
                    ))}

                    <th className="py-3 px-3 font-semibold whitespace-nowrap text-center bg-green-800">Present</th>
                    <th className="py-3 px-3 font-semibold whitespace-nowrap text-center bg-red-800">Absent</th>
                    {empType === 'permanent' && (
                      <th className="py-3 px-3 font-semibold whitespace-nowrap text-center bg-orange-700">Half Day</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((emp, idx) => (
                    <tr key={emp.empId || idx} className="hover:bg-purple-50 transition-colors">
                      <td className="py-2.5 px-3 text-gray-500 sticky left-0 bg-white z-10">
                        {(currentPage - 1) * 100 + idx + 1}
                      </td>
                      <td
                        className="py-2.5 px-3 font-medium text-gray-800 sticky left-10 bg-white z-10 cursor-pointer hover:text-[#6A1786] min-w-[160px]"
                        onClick={() => navigate(`/attendance/employee/${emp.empId}`)}
                      >
                        {emp.empName}
                      </td>
                      <td className="py-2.5 px-3 text-gray-600">{emp.empId || '—'}</td>
                      <td className="py-2.5 px-3 text-gray-600 min-w-[140px]">{emp.kvName}</td>
                      <td className="py-2.5 px-3 text-gray-600 min-w-[100px]">{emp.roName}</td>
                      <td className="py-2.5 px-3 text-gray-600 min-w-[100px]">{emp.stateName}</td>

                      {/* Day cells */}
                      {dayNumbers.map(d => {
                        const ds = emp.days[d];
                        const meta = STATUS_META[ds?.status ?? 'NM'];
                        return (
                          <td key={d} className="py-2.5 px-1 text-center">
                            <span className={`inline-block w-7 h-5 leading-5 rounded text-[10px] font-bold ${meta.bg} ${meta.text}`}>
                              {meta.label}
                            </span>
                          </td>
                        );
                      })}

                      <td className="py-2.5 px-3 text-center font-semibold text-green-700">{emp.totalPresent}</td>
                      <td className="py-2.5 px-3 text-center font-semibold text-red-600">{emp.totalAbsent}</td>
                      {empType === 'permanent' && (
                        <td className="py-2.5 px-3 text-center font-semibold text-orange-600">{emp.totalHalfDay}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && !loading && !error && (
          <div className="flex items-center justify-between mt-4 px-1">
            <p className="text-sm text-gray-500">
              Page <span className="font-semibold text-[#6A1786]">{currentPage}</span> of{' '}
              <span className="font-semibold">{totalPages}</span> &nbsp;·&nbsp;
              <span className="font-semibold">{totalRecords.toLocaleString()}</span> total records
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                First
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1.5 border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                return startPage + i;
              }).map(p => (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  className={`px-3 py-1.5 text-xs border rounded-lg transition ${
                    p === currentPage
                      ? 'bg-[#6A1786] text-white border-[#6A1786] font-semibold'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {p}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1.5 border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Last
              </button>
              <div className="flex items-center gap-2 px-2">
                <input
                  type="text"
                  value={jumpPage}
                  onChange={e => setJumpPage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleJumpToPage();
                    }
                  }}
                  placeholder="Page"
                  className="w-16 px-2 py-1 text-xs border border-gray-200 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-purple-300"
                />
                <button
                  onClick={handleJumpToPage}
                  disabled={loading || !jumpPage}
                  className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Go
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
